import type {
  IInsight,
  IInstrumentInsightCard,
  IInstrumentInsightsPanel,
} from '@brushfire/types'

export interface NormalizedInstrumentInsights {
  insights: IInsight[]
  placeholderMessage: string | null
  suppressedFindingsNote: string | null
  excessFindingsNote: string | null
  insightGenerationInProgress: boolean
  insightGenerationFailed: boolean
}

function isInsightsPanel(value: unknown): value is IInstrumentInsightsPanel {
  return (
    typeof value === 'object' &&
    value !== null &&
    'cards' in value &&
    Array.isArray((value as IInstrumentInsightsPanel).cards)
  )
}

function mapPanelCardToInsight(
  card: IInstrumentInsightCard,
  campaignId: string,
  instrumentId: string,
): IInsight {
  return {
    _id: card.id,
    campaignId,
    instrumentId,
    claim: card.heading,
    methodology: card.body,
    priority: 'MEDIUM',
    sourceTags: [],
    suppressedNote: null,
    competing_interpretations: [],
    confidenceScore: 0,
    evidenceLinks: [],
    isFinalAnalysis: false,
    notes: (card.notes ?? []).map((note) => ({
      _id: note._id,
      authorId: note.authorId,
      authorName: note.authorName,
      authorRole: note.authorRole,
      text: note.text,
      createdAt: new Date(note.createdAt),
      updatedAt: new Date(note.updatedAt),
    })),
    lastEvaluatedAt: new Date(card.lastEvaluatedAt),
    createdAt: new Date(card.lastEvaluatedAt),
  }
}

/** Normalize brownfield `IInsight[]` or BFP-654 `InstrumentInsightsPanel` API payload. */
export function normalizeInstrumentInsightsResponse(
  data: IInstrumentInsightsPanel | IInsight[] | null | undefined,
  campaignId: string,
  instrumentId: string,
): NormalizedInstrumentInsights {
  if (Array.isArray(data)) {
    const suppressedFindingsNote =
      data.find((row) => row.suppressedNote)?.suppressedNote ?? null
    return {
      insights: data,
      placeholderMessage: null,
      suppressedFindingsNote,
      excessFindingsNote: null,
      insightGenerationInProgress: false,
      insightGenerationFailed: false,
    }
  }

  if (isInsightsPanel(data)) {
    return {
      insights: data.cards.map((card) => mapPanelCardToInsight(card, campaignId, instrumentId)),
      placeholderMessage: data.placeholderMessage ?? null,
      suppressedFindingsNote: data.suppressedFindingsNote ?? null,
      excessFindingsNote: data.excessFindingsNote ?? null,
      insightGenerationInProgress: data.insightGenerationInProgress === true,
      insightGenerationFailed: data.insightGenerationFailed === true,
    }
  }

  return {
    insights: [],
    placeholderMessage: null,
    suppressedFindingsNote: null,
    excessFindingsNote: null,
    insightGenerationInProgress: false,
    insightGenerationFailed: false,
  }
}
